/**
 * /me/travel/active — Session 3d PR 25.
 * Owning agent: Khidmat.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { session3dApi } from "../lib/session3d-api";
import type { TravelTripItem } from "../lib/session3d-api";
import { MeListPage } from "./_MeListPage";
import type { MeColumn } from "./_MeListPage";
import { useCurrentEmployeeId } from "./_session3dShell";

export function MyActiveTripPage() {
  const { t } = useTranslation();
  const employeeId = useCurrentEmployeeId();

  const query = useQuery({
    queryKey: ["s3d-travel-active", employeeId],
    queryFn: () => session3dApi.listMyTrips(employeeId),
    enabled: !!employeeId,
  });

  const items = query.data?.items ?? [];
  const counts = useMemo(() => ({
    total: items.length,
    attention: 0,
    critical: 0,
  }), [items]);

  const columns: MeColumn<TravelTripItem>[] = [
    {
      headerKey: "me.travel-active.col.destination",
      label: "Destination",
      render: (r) => <span className="font-medium">{r.destination}</span>,
    },
    {
      headerKey: "me.travel-active.col.dates",
      label: "Dates",
      render: (r) => <span dir="ltr" className="font-mono text-xs">{r.departure_date} → {r.return_date}</span>,
    },
    {
      headerKey: "me.travel-active.col.purpose",
      label: "Purpose",
      render: (r) => <span className="text-xs text-text-secondary">{r.purpose}</span>,
    },
    {
      headerKey: "me.travel-active.col.per_diem",
      label: "Per-diem received (SAR)",
      render: (r) => <span className="font-mono text-xs">{(r.per_diem_received_sar ?? 0).toFixed(2)}</span>,
    },
    {
      headerKey: "me.travel-active.col.state",
      label: "State",
      render: (r) => <span className="text-xs">{r.state}</span>,
    },
  ];

  return (
    <MeListPage<TravelTripItem>
      title={t("me.travel-active.title", "My Active Trip")}
      subtitle={t("me.travel-active.subtitle", "Current in-flight trip details + reconciliation prompts.")}
      domain="travel-active"
      khidmatPrefill={t("me.travel-active.ask_khidmat", "What's the status of my current trip?")}
      query={query}
      columns={columns}
      rowKey={(r) => r.trip_id}
      briefContext={{
        total: counts.total,
        attention: counts.attention,
        critical: counts.critical,
        top_kind: t("me.travel-active.title", "My Active Trip"),
        window_label: t("me.travel-active.window", "in flight"),
      }}
      emptyHeadline={t("me.travel-active.empty_headline", "No active travel.")}
      emptyBody={t("me.travel-active.empty_body", "Khidmat: nothing in flight right now.")}
    />
  );
}
